import { useState, useEffect, useCallback } from 'react';

interface UseCountUpOptions {
  start?: number;
  end: number;
  duration?: number;
  delay?: number;
  enabled?: boolean;
}

export const useCountUp = ({
  start = 0,
  end,
  duration = 2000,
  delay = 0,
  enabled = true,
}: UseCountUpOptions): number => {
  const [count, setCount] = useState(start);

  const animate = useCallback(() => {
    const startTime = performance.now();
    const range = end - start;

    const easeOutQuart = (t: number): number => {
      return 1 - Math.pow(1 - t, 4);
    };

    const updateCount = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentCount = Math.round(start + range * easedProgress);

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      }
    };

    requestAnimationFrame(updateCount);
  }, [start, end, duration]);

  useEffect(() => {
    if (!enabled) {
      setCount(start);
      return;
    }

    const timeoutId = setTimeout(() => {
      animate();
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [enabled, animate, delay, start]);

  return count;
};

export default useCountUp;
